import { Option } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import * as getShipmentOptionsSCPlus from "../../../pages/private/SupplyChainPlus/Shipments/getShipmentOptions";
import {
  ShipmentType,
  ShipmentStatus,
  CrossDockStatus,
  ShipmentLoadType,
  Incoterm,
  ShipmentServiceType,
  ShipmentTrackingStatus,
} from "../../../util/enums";

const getShipmentOptions = (isFmVersion = true) => {
  const { t: translate } = useTranslation();
  const { statusOptions: statusOptionsSCPlus } = getShipmentOptionsSCPlus.default();

  const typeOptions: Option[] = [
    { value: ShipmentType.AIR, label: translate("air") },
    { value: ShipmentType.RAIL, label: translate("rail") },
    { value: ShipmentType.ROAD, label: translate("road") },
    { value: ShipmentType.SEA, label: translate("sea") },
  ].sort(({ label: a }, { label: b }) => a.localeCompare(b));

  const draftStatus = { value: ShipmentStatus.DRAFT, label: translate("draft") };

  const statusOptions: Option[] = [draftStatus, ...statusOptionsSCPlus].sort(
    ({ label: a }, { label: b }) => a.localeCompare(b)
  );

  const seaStatusOptions: Option[] = [
    draftStatus,
    { value: ShipmentStatus.BOOKED, label: "Booked" },
    { value: ShipmentStatus.CANCELLED, label: "Cancelled" },
    { value: ShipmentStatus.SHIPPED, label: "Shipped" },
    { value: ShipmentTrackingStatus.COLLECTED_FROM_SHIPPER, label: "Collected from Shipper" },
    {
      value: ShipmentTrackingStatus.NOT_ARRIVED_AT_PORT_OF_LOAD,
      label: "Not arrived at Port of Load",
    },
    { value: ShipmentTrackingStatus.WAITING_AT_PORT_OF_LOAD, label: "Waiting at Port of Load" },
    { value: ShipmentTrackingStatus.SHIPPED_ON_BOARD, label: "Shipped on board" },
    { value: ShipmentTrackingStatus.UNDERWAY, label: "Underway" },
    {
      value: ShipmentTrackingStatus.UNDERWAY_TO_A_TRANSHIPMENT_PORT,
      label: "Underway to a Transhipment Port",
    },
    {
      value: ShipmentTrackingStatus.ARRIVAL_AT_TRANSHIPMENT_PORT,
      label: "Arrival at Transhipment Port",
    },
    {
      value: ShipmentTrackingStatus.WAITING_AT_TRANSHIPMENT_PORT,
      label: "Waiting at Transhipment Port",
    },
    { value: ShipmentTrackingStatus.LEFT_THE_DESTINATION_PORT, label: "Left the Destination Port" },
    { value: ShipmentTrackingStatus.SHIPMENT_COMPLETED, label: "Shipment completed" },
    { value: ShipmentTrackingStatus.WAITING_FOR_UPDATE, label: "Waiting for update" },
    { value: ShipmentTrackingStatus.TRACKING_NOT_AVAILABLE, label: "Tracking not available" },
  ];

  const allShipmentStatusOprions = [...statusOptions, ...seaStatusOptions];

  const containerTypeOptions: Option[] = [
    { value: "20_dry_standard", label: `20" ${translate("dryStandard")}` },
    { value: "40_dry_standard", label: `40" ${translate("dryStandard")}` },
    { value: "40_dry_high", label: `40" ${translate("dryHigh")}` },
    { value: "45_dry_high", label: `45" ${translate("dryHigh")}` },
    { value: "20_tank", label: `20" ${translate("tank")}` },
    { value: "40_tank", label: `40" ${translate("tank")}` },
    { value: "20_open_top", label: `20" ${translate("openTop")}` },
    { value: "40_open_top", label: `40" ${translate("openTop")}` },
    { value: "40_open_top_high", label: `40" ${translate("openTopHigh")}` },
    { value: "40_flat_standard", label: `40" ${translate("flatStandard")}` },
    { value: "40_flat_high", label: `40" ${translate("flatHigh")}` },
    { value: "20_flat", label: `20" ${translate("flat")}` },
    { value: "20_reefer_standard", label: `20" ${translate("reeferStandard")}` },
    { value: "40_reefer_high", label: `40" ${translate("reeferHigh")}` },
  ].sort(({ label: a }, { label: b }) => a.localeCompare(b));

  const crossDcokStatusOption: Option[] = [
    { value: CrossDockStatus.RECEIVED_EXPECTED, label: translate("receivedExpected") },
    { value: CrossDockStatus.RECEIVED_UNEXPECTED, label: translate("receivedUnexpected") },
    { value: CrossDockStatus.NOT_RECEIVED, label: translate("notReceivedExpected") },
  ];

  const serviceTypeOptions: Option[] = [
    { value: ShipmentServiceType.REGULAR_CARGO, label: translate("regularCargo") },
    { value: ShipmentServiceType.HAZARDOUS_CARGO, label: translate("hazardousCargo") },
    { value: ShipmentServiceType.FOOD_GRADE_CARGO, label: translate("foodGradeCargo") },
    { value: ShipmentServiceType.RARE_CARGO, label: translate("rareCargo") },
    { value: ShipmentServiceType.REEFER_CARGO, label: translate("reeferCargoRefrigerated") },
  ];

  const shipTermsOptions: Option[] = isFmVersion
    ? [
        { value: "CFS/CY", label: "CFS/CY" },
        { value: "CY/CY", label: "CY/CY" },
        { value: " CFS/CFS", label: " CFS/CFS" },
      ]
    : [
        { value: "CY/CY", label: "CY/CY" },
        { value: "CFS/CY", label: "CFS/CY" },
      ];

  const deliverToOptions: Option[] = [
    { value: "port", label: translate("port") },
    { value: "cfs", label: translate("cfs") },
  ];

  const sizeOptions: Option[] = [
    { value: "20DRY/20REEFER/20HNOR", label: "20DRY/20REEFER/20HNOR" },
    { value: "40DRY", label: "40DRY" },
    { value: "40HIGH", label: "40HIGH" },
    { value: "40REEFER/40HNOR", label: "40REEFER/40HNOR" },
    { value: "45HIGH", label: "45HIGH" },
  ];

  const boundOptions: Option[] = [
    { value: "Inbound", label: "Inbound" },
    { value: "Outbound", label: "Outbound" },
  ];

  const airLoadTypeOptions: Option[] = [
    { value: ShipmentLoadType.ULD_CONTAINERS, label: translate("uldContainers") },
    { value: ShipmentLoadType.ULD_PALLETS, label: translate("uldPallets") },
  ];

  const roadLoadTypeOptions: Option[] = [
    { value: ShipmentLoadType.FTL, label: translate("ftl") },
    { value: ShipmentLoadType.LTL, label: translate("ltl") },
    { value: ShipmentLoadType.GROUPAGE_VANLINE, label: translate("groupageVanline") },
  ];

  const seaLoadTypeOptions: Option[] = [
    { value: ShipmentLoadType.FCL, label: translate("fcl") },
    { value: ShipmentLoadType.LCL, label: translate("lcl") },
    { value: ShipmentLoadType.GROUPAGE, label: translate("groupage") },
    { value: ShipmentLoadType.RORO, label: translate("roro") },
    { value: ShipmentLoadType.BULK, label: translate("bulk") },
  ];

  const railLoadTypeOptions: Option[] = [];

  const incotermOptions: Option[] = [
    { value: Incoterm.CIP, label: Incoterm.CIP },
    { value: Incoterm.CPT, label: Incoterm.CPT },
    { value: Incoterm.DAP, label: Incoterm.DAP },
    { value: Incoterm.DDP, label: Incoterm.DDP },
    { value: Incoterm.DPU, label: Incoterm.DPU },
    { value: Incoterm.EXW, label: Incoterm.EXW },
    { value: Incoterm.FCA, label: Incoterm.FCA },
  ];

  const seaIncotermOptions: Option[] = [
    { value: Incoterm.CFR, label: Incoterm.CFR },
    { value: Incoterm.CIF, label: Incoterm.CIF },
    ...incotermOptions,
    { value: Incoterm.FAS, label: Incoterm.FAS },
    { value: Incoterm.FOB, label: Incoterm.FOB },
  ];

  return {
    typeOptions,
    statusOptions,
    containerTypeOptions,
    crossDcokStatusOption,
    serviceTypeOptions,
    shipTermsOptions,
    deliverToOptions,
    sizeOptions,
    boundOptions,
    airLoadTypeOptions,
    roadLoadTypeOptions,
    seaLoadTypeOptions,
    railLoadTypeOptions,
    incotermOptions,
    seaIncotermOptions,
    seaStatusOptions,
    allShipmentStatusOprions,
  };
};

export default getShipmentOptions;
